import type { Cell } from "../../game-core/src/index";

export type Accent = "coral" | "cyan" | "amber" | "violet" | "lime";

export type HazardKind =
  | "none"
  | "pulse-lanes"
  | "ember-drift"
  | "tide-rise"
  | "closing-ring"
  | "skyline-crossfire";

export type ObjectiveKind =
  "breakthrough" | "relay" | "survive" | "control" | "precision" | "warden";

export type Difficulty = 1 | 2 | 3 | 4 | 5;

export type SymmetryKind = "rotational" | "mirrored" | "lane-balanced";

export type DestructibleDensity = "light" | "medium" | "heavy";

export type SpawnId = "p1" | "p2" | "p3" | "p4";

export const SPAWN_IDS: readonly SpawnId[] = Object.freeze([
  "p1",
  "p2",
  "p3",
  "p4",
]);

export interface SpawnManifest {
  readonly p1: Cell;
  readonly p2: Cell;
  readonly p3: Cell;
  readonly p4: Cell;
}

export interface SpawnSafetyMetadata {
  readonly verified: boolean;
  readonly clearRadius: number;
  readonly minimumEscapeRoutes: number;
  readonly notes: string;
}

export interface FairnessMetadata {
  readonly symmetry: SymmetryKind;
  readonly destructibleDensity: DestructibleDensity;
  readonly spawnSafety: SpawnSafetyMetadata;
}

export interface MapManifest {
  readonly id: string;
  readonly name: string;
  readonly circuitId: string;
  readonly stage: number;
  readonly tagline: string;
  readonly accent: Accent;
  readonly rows: readonly string[];
  readonly spawns: SpawnManifest;
  readonly hazard: HazardKind;
  readonly objective: ObjectiveKind;
  readonly difficulty: Difficulty;
  readonly boss: boolean;
  readonly fairness: FairnessMetadata;
}

export interface CircuitStageDefinition {
  readonly stage: number;
  readonly mapId: string;
}

export interface CircuitDefinition {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly accent: Accent;
  readonly stages: readonly CircuitStageDefinition[];
  readonly bossMapId: string;
}

export interface CampaignDefinition {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly contentVersion: string;
  readonly circuits: readonly CircuitDefinition[];
  readonly maps: readonly MapManifest[];
}

export type ChallengeMode =
  "time-attack" | "survival" | "puzzle" | "precision" | "daily-seed";

export interface ChallengeDefinition {
  readonly id: string;
  readonly name: string;
  readonly mode: ChallengeMode;
  readonly description: string;
  readonly mapId: string;
  readonly objective: ObjectiveKind;
  readonly difficulty: Difficulty;
  readonly seed: string;
  readonly target: number;
}

export type TournamentFormat = "single-elimination" | "round-robin";

export interface TournamentRoundDefinition {
  readonly round: number;
  readonly mapPool: readonly string[];
}

export interface TournamentDefinition {
  readonly id: string;
  readonly name: string;
  readonly format: TournamentFormat;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  readonly rounds: readonly TournamentRoundDefinition[];
}

export interface InvalidRowReport {
  readonly row: number;
  readonly expectedWidth: number;
  readonly actualWidth: number;
  readonly reason: string;
}

export interface UnsafeSpawnReport {
  readonly player: SpawnId;
  readonly cell: Cell | null;
  readonly reason: string;
}

export interface UnreachableFloorRegion {
  readonly anchor: Cell;
  readonly size: number;
  readonly cells: readonly Cell[];
}

export interface MapValidationReport {
  readonly mapId: string;
  readonly valid: boolean;
  readonly invalidRows: readonly InvalidRowReport[];
  readonly unsafeSpawns: readonly UnsafeSpawnReport[];
  readonly unreachableFloorRegions: readonly UnreachableFloorRegion[];
  readonly errors: readonly string[];
}

export interface CampaignValidationReport {
  readonly valid: boolean;
  readonly mapCount: number;
  readonly maps: readonly MapValidationReport[];
  readonly invalidMaps: readonly MapValidationReport[];
  readonly errors: readonly string[];
}
